import React, { useState } from 'react'

/**
 * Simple grouped bar chart for dashboard use.
 *
 * Props:
 *  - labels: string[]
 *  - series: [{ name, color, values: number[] }]
 *  - showGrid?: boolean
 *  - height?: number
 *  - width?: number
 *  - maxXTicks?: number
 *  - yTicks?: number
 *  - xLabelFormatter?: (label, index) => string
 *  - yFormatter?: (value) => string | number
 *  - paddingLeft?: number
 *  - paddingRight?: number
 *  - onBarClick?: (label, index, series) => void
 *  - hiddenSeries?: Set<string> (series names to hide)
*/
export default function MultiBarChart({
  labels,
  series,
  showGrid = true,
  height = 260,
  width = 800,
  maxXTicks = 12,
  yTicks = 6,
  xLabelFormatter,
  yFormatter,
  paddingLeft = 56,
  paddingRight = 16,
  onBarClick,
  hiddenSeries = new Set()
}) {
  const [tooltip, setTooltip] = useState(null)
  const [hoveredBar, setHoveredBar] = useState(null)

  const safeLabels = []
  if (Array.isArray(labels)) {
    for (let i = 0; i < labels.length; i++) {
      const v = labels[i]
      safeLabels.push(v == null ? '' : String(v))
    }
  }

  const safeSeries = []
  if (Array.isArray(series)) {
    for (let i = 0; i < series.length; i++) {
      const s = series[i] || {}
      const name = s.name != null ? String(s.name) : 'Series ' + (i + 1)
      const color = s.color || '#3b82f6'
      const vals = []
      if (Array.isArray(s.values)) {
        for (let j = 0; j < s.values.length; j++) {
          const n = Number(s.values[j])
          vals.push(Number.isFinite(n) ? n : 0)
        }
      }
      // Only include series that are not hidden
      if (!hiddenSeries.has(name)) {
        safeSeries.push({ name, color, values: vals })
      }
    }
  }

  let pointCount = safeLabels.length
  for (let i = 0; i < safeSeries.length; i++) {
    if (safeSeries[i].values.length > pointCount) {
      pointCount = safeSeries[i].values.length
    }
  }

  if (!safeSeries.length || pointCount === 0) {
    return (
      <div className="muted" style={{ padding: 8 }}>
        No data.
      </div>
    )
  }

  const xLabels = new Array(pointCount)
  for (let i = 0; i < pointCount; i++) {
    xLabels[i] = i < safeLabels.length ? safeLabels[i] : ''
  }

  const paddingTop = 16
  const paddingBottom = 32
  const innerW = Math.max(10, width - paddingLeft - paddingRight)
  const innerH = Math.max(10, height - paddingTop - paddingBottom)

  let minY = 0
  let maxY = 0
  let have = false

  for (let i = 0; i < safeSeries.length; i++) {
    const vals = safeSeries[i].values
    for (let j = 0; j < vals.length; j++) {
      const n = vals[j]
      if (!Number.isFinite(n)) continue
      if (!have) {
        minY = maxY = n
        have = true
      } else {
        if (n < minY) minY = n
        if (n > maxY) maxY = n
      }
    }
  }

  if (!have) {
    minY = 0
    maxY = 1
  }
  if (minY > 0) minY = 0
  if (maxY < 0) maxY = 0
  if (minY === maxY) {
    if (maxY === 0) {
      maxY = 1
    } else if (maxY > 0) {
      minY = 0
    } else {
      maxY = 0
    }
  }

  const spanY = maxY - minY

  const scaleY = (v) => {
    const n = Number(v)
    if (!Number.isFinite(n)) return paddingTop + innerH
    const t = (n - minY) / spanY
    return paddingTop + innerH - t * innerH
  }

  const groupW = pointCount > 0 ? innerW / pointCount : innerW
  const seriesCount = Math.max(1, safeSeries.length)
  const gap = Math.max(2, Math.min(8, groupW * 0.08))
  const barW = Math.max(1, (groupW - gap * (seriesCount + 1)) / seriesCount)

  const maxTicks = maxXTicks && maxXTicks > 0 ? maxXTicks : 12
  const xStep =
    pointCount > maxTicks ? Math.max(1, Math.floor(pointCount / maxTicks)) : 1

  const defaultYFormatter = (n) => {
    const v = Number(n) || 0
    const big = Math.abs(v) >= 1000
    return v.toLocaleString(
      undefined,
      big
        ? { maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    )
  }
  const fmtY = typeof yFormatter === 'function' ? yFormatter : defaultYFormatter
  const fmtX =
    typeof xLabelFormatter === 'function' ? xLabelFormatter : (s) => s

  const renderYGrid = () => {
    const nodes = []
    const ticks = yTicks > 0 ? yTicks : 4
    for (let i = 0; i <= ticks; i++) {
      const t = i / ticks
      const y = paddingTop + innerH - t * innerH
      const val = minY + (maxY - minY) * t
      nodes.push(
        <g key={i}>
          {showGrid && (
            <line
              x1={paddingLeft}
              x2={paddingLeft + innerW}
              y1={y}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          )}
          <text
            x={paddingLeft - 6}
            y={y + 3}
            textAnchor="end"
            fontSize="9"
            fill="#6b7280"
          >
            {fmtY(val)}
          </text>
        </g>
      )
    }
    return nodes
  }

  const renderBars = () => {
    const groups = []
    const baseline = scaleY(0)
    for (let i = 0; i < pointCount; i++) {
      const baseX = paddingLeft + i * groupW
      for (let s = 0; s < safeSeries.length; s++) {
        const val = Number(safeSeries[s].values[i])
        if (!Number.isFinite(val)) continue
        const x = baseX + gap + s * (barW + gap)
        const y = scaleY(val)
        const top = Math.min(y, baseline)
        const h = Math.abs(baseline - y)
        if (h <= 0.5) continue

        const barKey = `${i}-${s}`
        const isHovered = hoveredBar === barKey

        groups.push(
          <rect
            key={barKey}
            x={x}
            y={top}
            width={barW}
            height={h}
            fill={safeSeries[s].color}
            fillOpacity={isHovered ? 1 : 0.85}
            rx="2"
            ry="2"
            style={{
              cursor: onBarClick ? 'pointer' : 'default',
              transition: 'fill-opacity 0.2s ease'
            }}
            onMouseEnter={(e) => {
              setHoveredBar(barKey)
              const rect = e.target.getBoundingClientRect()
              const allSeriesData = series
                .filter(ser => !hiddenSeries.has(ser.name))
                .map((ser, idx) => ({
                  name: ser.name,
                  value: ser.values[i] || 0,
                  color: ser.color
                }))

              setTooltip({
                x: rect.left + rect.width / 2,
                y: rect.top,
                label: xLabels[i],
                series: allSeriesData
              })
            }}
            onMouseLeave={() => {
              setHoveredBar(null)
              setTooltip(null)
            }}
            onClick={() => {
              if (onBarClick) {
                onBarClick(xLabels[i], i, safeSeries[s])
              }
            }}
          />
        )
      }
    }
    return groups
  }

  const renderXLabels = () => {
    const nodes = []
    for (let i = 0; i < pointCount; i++) {
      if (i % xStep !== 0 && i !== 0 && i !== pointCount - 1) continue
      const raw = xLabels[i]
      const formatted = fmtX(raw, i)
      if (formatted == null || formatted === '') continue
      const x = paddingLeft + i * groupW + groupW / 2
      const y = paddingTop + innerH + 14
      nodes.push(
        <text
          key={i}
          x={x}
          y={y}
          textAnchor="middle"
          fontSize="9"
          fill="#6b7280"
        >
          {formatted}
        </text>
      )
    }
    return nodes
  }

  const axisY = scaleY(0)

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height, overflow: 'visible' }}
      >
        {renderYGrid()}

        <line
          x1={paddingLeft}
          x2={paddingLeft + innerW}
          y1={axisY}
          y2={axisY}
          stroke="#9ca3af"
          strokeWidth="1"
        />
        <line
          x1={paddingLeft}
          x2={paddingLeft}
          y1={paddingTop}
          y2={paddingTop + innerH}
          stroke="#9ca3af"
          strokeWidth="1"
        />

        {renderBars()}
        {renderXLabels()}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="chart-tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y - 10,
            transform: 'translate(-50%, -100%)',
            background: 'white',
            border: '1px solid #d1d5db',
            padding: '8px 12px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none',
            zIndex: 1000,
            minWidth: '150px'
          }}
        >
          <div style={{
            fontWeight: 600,
            marginBottom: 6,
            fontSize: '12px',
            color: '#374151',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: 4
          }}>
            {tooltip.label}
          </div>
          {tooltip.series.map((s, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 4,
                fontSize: '11px'
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: s.color,
                  borderRadius: '2px',
                  flexShrink: 0
                }}
              />
              <span style={{ color: '#6b7280', minWidth: '60px' }}>{s.name}:</span>
              <span style={{ fontWeight: 600, color: '#111827', marginLeft: 'auto' }}>
                {Number(s.value).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
